/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 * 
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 * 
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */
import {
    IInstitution,
    QueriesContext,
    QueryFlag,
    usePatientStates
} from '@ngt/opms';

import { DateTime } from 'luxon';

import { Box, Chip } from '@mui/material';
import styled from '@mui/material/styles/styled';
import { GridColDef } from '@mui/x-data-grid';
import { useContext, useMemo } from 'react';
import { Patient } from '../../api/dtos';
import useQuerySummaries from '../data/useQuerySummaries';


const StyledChip = styled(Chip)(({theme}) => ({
    backgroundColor: '#987ba7',
    color: theme.palette.common.white,
}));

export const useRegisteredPatientColumns = (institutions: IInstitution[] | null) => {
    const {data: states} = usePatientStates();
    const {data: querySummaries} = useQuerySummaries();
    const {data: queries, ...queriesContext} = useContext(QueriesContext);

    const columns: GridColDef<Patient>[] = useMemo(() => {
        const c: (GridColDef<Patient> | boolean)[] = [  
            {
                field: "queryFlag",
                headerName: "",
                renderCell: (params) => {
                    const patientQuerySummaries = querySummaries?.filter(qs => qs.patientId == params.row.id);
                    const patientQueries = queries?.filter(q => patientQuerySummaries?.some(qs => q.id == qs.id))
                    
                    return (
                        <QueriesContext.Provider value={{data: patientQueries, ...queriesContext}}>
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    height: "100%"
                                }}
                            >
                                <QueryFlag
                                    disableClick
                                />
                            </Box>
                        </QueriesContext.Provider>
                    );
                },
                sortable: false,
                filterable: false
            },
            {
                field: 'studyNumber',
                headerName: 'Study Number',
                width: 150
            },
            {
                field: 'institutionCode',
                headerName: 'Site Code',
                renderCell: (params) => {
                    return (
                        <StyledChip
                            label={params.row.institutionCode}
                            size="small"
                        />
                    );
                },
                width: 110
            },
            {
                field: 'institutionId',
                headerName: 'Institution',
                valueGetter: (_value, row) =>  institutions?.find(i => i.id === row.institutionId)?.name,
                flex: 1
            },
            {
                field: 'patientStateId',
                headerName: 'Status',
                valueFormatter: (_value, row) => states?.find(state => state.id == row.patientStateId)?.name,
                width: 200
            },
            {
                field: 'registrationDate',
                headerName: 'Registration Date',
                renderCell: rowData => (
                    rowData.row.registrationDate ? DateTime.fromISO(rowData.row.registrationDate).toFormat('dd-MMM-yyyy') : "N/A"
                ),
                width: 200
            }
        ];

        return c.filter(column => !!column) as GridColDef<Patient>[];
    }, [institutions, states, querySummaries, queries]);

    return columns;
};

/*
 * ---------------------------------------------------------------------------------
 * Default Export
 * ---------------------------------------------------------------------------------
 */